// batch-71 verplichte-certificaten (FO-541, spec 241) - verplichte cursussen en
// certificaten als doorlopende eis voor iedereen in dienst, niet alleen tijdens onboarding.
//
// De eisen staan in EEN tabel (`VerplichtCertificaat`, hernoemd uit de onboarding-lijst). De
// stand per (medewerker, eis) is afgeleid en herbouwbaar: de pure regels staan in
// `packages/utils/src/verplichte-certificaten.ts`, de opslag in
// `apps/api/src/lib/verplichte-certificaten/`.
//
// GEDEELDE schema's: de API parset ermee en het formulier valideert er client-side mee
// (CLAUDE.md "Validatie & foutmeldingen").

import { z } from 'zod'
import type { CertificaatTypeStatus } from './hrm'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import {
  ONBOARDING_CERTIFICAAT_DEADLINE,
  type OnboardingCertificaatDeadline,
} from './onboarding-startworkflow'

// ─── Groepen ────────────────────────────────────────────────────────────────

export const CERTIFICAAT_GROEPEN = ['VCA', 'NEN3140', 'HOOGWERKER', 'HEFTRUCK', 'BHV'] as const
export type CertificaatGroep = (typeof CERTIFICAAT_GROEPEN)[number]

export const CERTIFICAAT_GROEP_LABELS: Record<CertificaatGroep, string> = {
  VCA: 'VCA',
  NEN3140: 'NEN 3140',
  HOOGWERKER: 'Hoogwerker',
  HEFTRUCK: 'Heftruck',
  BHV: 'BHV',
}

/**
 * De koppeling certificaattype -> groep op code. Spiegel van de UPDATE's in migratie
 * b71_verplichte_certificaten (de deploy seedt niet) en bron voor de seed; de test
 * `verplichte-certificaten-een-lijst-drift.test.ts` houdt ze gelijk. `NEN3140_VA` is de
 * historische sleutel van de VOP-cursus (FO-397).
 */
export const CERTIFICAAT_GROEP_TYPE_CODES: Record<CertificaatGroep, readonly string[]> = {
  VCA: ['VCA_BASIS', 'VCA_VOL'],
  NEN3140: ['NEN3140_VP', 'NEN3140_VA'],
  HOOGWERKER: ['HOOGWERKER_1A', 'HOOGWERKER_1B', 'HOOGWERKER_3A', 'HOOGWERKER_3B'],
  HEFTRUCK: ['HEFTRUCK', 'REACH_TRUCK', 'ORDERPICKER'],
  BHV: ['BHV'],
}

/** Korte uitleg per variant in de portal (spec 241 §D9). Op code; geen uitleg = alleen de naam. */
export const CERTIFICAAT_VARIANT_UITLEG: Readonly<Record<string, string>> = {
  VCA_BASIS: 'Voor wie uitvoerend werk doet (B-VCA).',
  VCA_VOL: 'Voor wie operationeel leiding geeft (VOL-VCA).',
  NEN3140_VP: 'Vakbekwaam persoon: je werkt zelfstandig aan elektrische installaties.',
  NEN3140_VA: 'Voldoende onderricht persoon: je werkt onder aanwijzing aan of bij installaties.',
  HOOGWERKER_1A: 'Verticaal, het platform blijft binnen het onderstel.',
  HOOGWERKER_1B: 'Gieken, het platform kan buiten het onderstel komen.',
  HOOGWERKER_3A: 'Zelfrijdend en verticaal (schaarlift).',
  HOOGWERKER_3B: 'Zelfrijdend met giek (knik- of telescooparm).',
  HEFTRUCK: 'Heftruck of vorkheftruck.',
  REACH_TRUCK: 'Reachtruck in het magazijn.',
  ORDERPICKER: 'Orderpicker in het magazijn.',
}

// ─── Signaal-instelling: standaardwaarden ───────────────────────────────────

/** Spec 241 A2: 5 kalendermaanden sinds `startDate`. */
export const VERPLICHT_HERINNERING_NA_MAANDEN_STANDAARD = 5
/** Spec 241 A4: elke 14 dagen. */
export const VERPLICHT_HERHAAL_DAGEN_STANDAARD = 14
/** Spec 241 A4: maximaal 3 keer, daarna HR en teamleider. */
export const VERPLICHT_MAX_HERINNERINGEN_STANDAARD = 3

// ─── Situaties ──────────────────────────────────────────────────────────────

/**
 * De situatie per (medewerker, eis) uit spec 241 §B4, plus de niet-gevallen. Dit is wat de
 * status-chip toont en waar de signalen op beslissen.
 */
export const VERPLICHT_SITUATIES = [
  'GELDIG',
  'VERLOOPT',
  'VERLOPEN',
  'GEPLAND',
  'IN_OPLEIDING',
  'GEVOLGD_WACHT_UPLOAD',
  'WACHT_OP_CONTROLE',
  'ONTBREEKT_TE_PLANNEN',
  'ONTBREEKT_UPLOAD',
  'START_ONBEKEND',
  'NIET_VAN_TOEPASSING',
] as const
export type VerplichtSituatie = (typeof VERPLICHT_SITUATIES)[number]

export const VERPLICHT_SITUATIE_LABELS: Record<VerplichtSituatie, string> = {
  GELDIG: 'Geldig',
  VERLOOPT: 'Verloopt',
  VERLOPEN: 'Verlopen',
  GEPLAND: 'Cursus gepland',
  IN_OPLEIDING: 'In opleiding',
  GEVOLGD_WACHT_UPLOAD: 'Gevolgd, wacht op upload',
  WACHT_OP_CONTROLE: 'Wacht op controle',
  ONTBREEKT_TE_PLANNEN: 'Ontbreekt, nog te plannen',
  ONTBREEKT_UPLOAD: 'Ontbreekt, upload gevraagd',
  START_ONBEKEND: 'Startdatum onbekend',
  NIET_VAN_TOEPASSING: 'N.v.t.',
}

export const VERPLICHT_SITUATIE_BADGE: Record<
  VerplichtSituatie,
  'success' | 'warning' | 'danger' | 'secondary' | 'info'
> = {
  GELDIG: 'success',
  VERLOOPT: 'warning',
  VERLOPEN: 'danger',
  GEPLAND: 'info',
  IN_OPLEIDING: 'info',
  GEVOLGD_WACHT_UPLOAD: 'warning',
  WACHT_OP_CONTROLE: 'info',
  ONTBREEKT_TE_PLANNEN: 'danger',
  ONTBREEKT_UPLOAD: 'danger',
  START_ONBEKEND: 'warning',
  NIET_VAN_TOEPASSING: 'secondary',
}

/**
 * De medewerker heeft geen geldig certificaat voor de eis en er loopt geen planning: de KPI
 * "zonder verplicht certificaat" telt medewerkers met minstens één zo'n eis. Gepland of
 * gevolgd is geen "ontbreekt" (spec 241 §B4), gevolgd-zonder-upload hoort hier wel: het
 * certificaat staat nog niet in het dossier.
 */
export const VERPLICHT_SITUATIES_ZONDER_CERTIFICAAT: readonly VerplichtSituatie[] = [
  'VERLOPEN',
  'GEVOLGD_WACHT_UPLOAD',
  'ONTBREEKT_TE_PLANNEN',
  'ONTBREEKT_UPLOAD',
  'START_ONBEKEND',
]

/** De telling "ontbreekt totaal" op de tab Verplicht: geen certificaat en niets gevolgd. */
export const VERPLICHT_SITUATIES_ONTBREEKT: readonly VerplichtSituatie[] = [
  'VERLOPEN',
  'ONTBREEKT_TE_PLANNEN',
  'ONTBREEKT_UPLOAD',
  'START_ONBEKEND',
]

/**
 * De chip-tekst: "Cursus gepland 12-12-2026", "Ontbreekt, upload gevraagd" of (nog geen
 * verzoek uit) "Ontbreekt, upload nog te vragen". Datums in Europe/Amsterdam.
 */
export function verplichtSituatieTekst(cel: {
  situatie: VerplichtSituatie
  cursusDatum: string | null
  geldigTot: string | null
  uploadVerzoekAantal: number
}): string {
  const datum = (iso: string): string =>
    new Intl.DateTimeFormat('nl-NL', {
      timeZone: 'Europe/Amsterdam',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(iso))
  switch (cel.situatie) {
    case 'GEPLAND':
      return cel.cursusDatum ? `Cursus gepland ${datum(cel.cursusDatum)}` : 'Cursus gepland'
    case 'VERLOOPT':
      return cel.geldigTot ? `Verloopt ${datum(cel.geldigTot)}` : 'Verloopt'
    case 'GELDIG':
      return cel.geldigTot ? `Geldig t/m ${datum(cel.geldigTot)}` : 'Geldig'
    case 'ONTBREEKT_UPLOAD':
      return cel.uploadVerzoekAantal > 0 ? 'Ontbreekt, upload gevraagd' : 'Ontbreekt, upload nog te vragen'
    default:
      return VERPLICHT_SITUATIE_LABELS[cel.situatie]
  }
}

/**
 * De chip-tekst voor de medewerker zelf (portal). Geen HR-taal zoals "upload nog te vragen" of
 * "startdatum onbekend": alleen wat er met zijn eigen certificaat is.
 */
export function verplichtSituatieTekstMedewerker(cel: {
  situatie: VerplichtSituatie
  cursusDatum: string | null
  geldigTot: string | null
  uploadVerzoekAantal: number
}): string {
  switch (cel.situatie) {
    case 'ONTBREEKT_UPLOAD':
    case 'ONTBREEKT_TE_PLANNEN':
    case 'START_ONBEKEND':
      return 'Ontbreekt'
    case 'GEVOLGD_WACHT_UPLOAD':
      return 'Gevolgd, upload je certificaat'
    case 'WACHT_OP_CONTROLE':
      return 'Bij HR in controle'
    case 'IN_OPLEIDING':
      return 'Cursus bezig'
    default:
      return verplichtSituatieTekst(cel)
  }
}

/**
 * Situaties die de medewerker om iets vragen. Vóór het HR-akkoord op het dry-run-rapport toont de
 * portal ze niet: de lijst kan dan nog de voorbeelden zijn (spec 241 §8).
 */
export const VERPLICHT_SITUATIES_VRAAG: readonly VerplichtSituatie[] = [
  'ONTBREEKT_UPLOAD',
  'ONTBREEKT_TE_PLANNEN',
  'START_ONBEKEND',
]

// ─── Cursusstatus ───────────────────────────────────────────────────────────

export const VERPLICHT_CURSUS_STATUSSEN = [
  'GEEN',
  'AANGEVRAAGD',
  'GEPLAND',
  'GEVOLGD_WACHT_OP_CERTIFICAAT',
  'GEVOLGD_WACHT_OP_CONTROLE',
] as const
export type VerplichtCursusStatus = (typeof VERPLICHT_CURSUS_STATUSSEN)[number]

export const VERPLICHT_CURSUS_STATUS_LABELS: Record<VerplichtCursusStatus, string> = {
  GEEN: 'Geen cursus bekend',
  AANGEVRAAGD: 'Cursus aangevraagd',
  GEPLAND: 'Cursus gepland',
  GEVOLGD_WACHT_OP_CERTIFICAAT: 'Gevolgd, wacht op certificaat',
  GEVOLGD_WACHT_OP_CONTROLE: 'Gevolgd, wacht op controle',
}

// ─── Signalen ───────────────────────────────────────────────────────────────

/**
 * De soorten signaal in het logboek (`VerplichtCertificaatSignaal.soort`). Idempotent per
 * (medewerker, eis, soort) met het herhaalinterval.
 */
export const VERPLICHT_SIGNAAL_SOORTEN = [
  'UPLOAD_VERZOEK',
  'NIET_AANGELEVERD',
  'GEVOLGD_HERINNERING',
  'NIET_OPGEPAKT',
  'TE_PLANNEN',
  'START_ONBEKEND',
] as const
export type VerplichtSignaalSoort = (typeof VERPLICHT_SIGNAAL_SOORTEN)[number]

export const VERPLICHT_SIGNAAL_UITKOMSTEN = ['MEDEWERKER', 'HR_TL'] as const
export type VerplichtSignaalUitkomst = (typeof VERPLICHT_SIGNAAL_UITKOMSTEN)[number]

// ─── Eisen: DTO + schema's ──────────────────────────────────────────────────

/** Een variant van een eis: een certificaattype dat aan de eis voldoet. */
export interface VerplichtVariantDTO {
  id: string
  name: string
  code: string
  /** Korte uitleg uit CERTIFICAAT_VARIANT_UITLEG, of null. */
  uitleg: string | null
}

/**
 * EEN rij op de lijst met verplichte eisen. `OnboardingVerplichtCertificaatDTO` is hiervan
 * een alias: de onboarding-startworkflow leest dezelfde tabel (spec 241 §A1).
 */
export interface VerplichtCertificaatDTO {
  id: string
  /** Exact type; null bij een groep-eis. */
  certificateTypeId: string | null
  certificateTypeNaam: string | null
  certificateTypeCode: string | null
  /** Groep; null bij een eis op een exact type. */
  certificaatGroep: CertificaatGroep | null
  /** Wat de gebruiker ziet: de groepsnaam of de typenaam. */
  label: string
  /** De typen die aan de eis voldoen. */
  varianten: VerplichtVariantDTO[]
  /** Leeg = elk type uit de groep voldoet. */
  geaccepteerdeTypeIds: string[]
  deadlineType: OnboardingCertificaatDeadline
  geldtVoorEmployeeTypes: string[]
  geldtVoorFunctietitels: string[]
  verplicht: boolean
  volgorde: number
  actief: boolean
  isVoorbeeld: boolean
  /** Null = nooit een uploadverzoek aan de medewerker, alleen signaleren. */
  herinneringNaMaanden: number | null
}

export const VERPLICHT_EMPLOYEE_TYPES = [
  'ONDERHOUD',
  'STORING',
  'SOFTWARE',
  'PROJECT',
  'UNIVERSEEL',
  'KANTOOR',
] as const

const geldtVoorEmployeeTypesSchema = z.array(z.enum(VERPLICHT_EMPLOYEE_TYPES)).max(6)
const geldtVoorFunctietitelsSchema = z.array(z.string().trim().min(1).max(120)).max(50)
const herinneringNaMaandenSchema = z.number().int().min(0).max(240).nullable()

/** Dit schema is bewust zonder `.partial()`: zod behoudt daar de defaults (FO-475). */
export const createVerplichtCertificaatSchema = z
  .object({
    certificateTypeId: idParam('certificateTypeId').optional(),
    certificaatGroep: z.enum(CERTIFICAAT_GROEPEN).optional(),
    geaccepteerdeTypeIds: z.array(idParam('geaccepteerdeTypeIds')).max(20).default([]),
    deadlineType: z.enum(ONBOARDING_CERTIFICAAT_DEADLINE).default('GEEN'),
    verplicht: z.boolean().default(true),
    volgorde: z.number().int().min(0).default(0),
    geldtVoorEmployeeTypes: geldtVoorEmployeeTypesSchema.default([]),
    geldtVoorFunctietitels: geldtVoorFunctietitelsSchema.default([]),
    herinneringNaMaanden: herinneringNaMaandenSchema.default(VERPLICHT_HERINNERING_NA_MAANDEN_STANDAARD),
  })
  .refine((v) => Boolean(v.certificateTypeId) !== Boolean(v.certificaatGroep), {
    message: 'Kies een certificaattype of een groep, niet allebei',
    path: ['certificateTypeId'],
  })
export type CreateVerplichtCertificaatInput = z.input<typeof createVerplichtCertificaatSchema>

export const updateVerplichtCertificaatSchema = z.object({
  deadlineType: z.enum(ONBOARDING_CERTIFICAAT_DEADLINE).optional(),
  verplicht: z.boolean().optional(),
  volgorde: z.number().int().min(0).optional(),
  actief: z.boolean().optional(),
  geaccepteerdeTypeIds: z.array(idParam('geaccepteerdeTypeIds')).max(20).optional(),
  geldtVoorEmployeeTypes: geldtVoorEmployeeTypesSchema.optional(),
  geldtVoorFunctietitels: geldtVoorFunctietitelsSchema.optional(),
  herinneringNaMaanden: herinneringNaMaandenSchema.optional(),
  /**
   * `false` neemt een voorbeeldregel over (HR heeft hem bekeken). `true` wordt genegeerd: een regel
   * wordt nooit weer een voorbeeld, maar de read-DTO mag wel terug zonder validatiefout.
   */
  isVoorbeeld: z.boolean().optional(),
})
export type UpdateVerplichtCertificaatInput = z.infer<typeof updateVerplichtCertificaatSchema>

// Aliassen: de onboarding-flow en zijn instellingenscherm blijven dezelfde lijst lezen.
export type OnboardingVerplichtCertificaatDTO = VerplichtCertificaatDTO
export const createOnboardingVerplichtCertificaatSchema = createVerplichtCertificaatSchema
export type CreateOnboardingVerplichtCertificaatInput = CreateVerplichtCertificaatInput
export const updateOnboardingVerplichtCertificaatSchema = updateVerplichtCertificaatSchema
export type UpdateOnboardingVerplichtCertificaatInput = UpdateVerplichtCertificaatInput

// ─── De stand: het contract voor lezers (FO-534, FO-537) ─────────────────────

/**
 * Eén rij van de afgeleide stand, zoals `leesVerplichteCertificaatStand(employeeId)` hem
 * teruggeeft (`apps/api/src/lib/verplichte-certificaten/stand.ts`). Dit is de ENE bron voor
 * de matrix, de portal, de kennismaking (FO-534) en de gesprekscontrole (FO-537): lees geen
 * eigen status uit `EmployeeCertificate`.
 *
 * Soft-deleted standrijen (uit dienst, vervallen eis) komen hier niet in.
 */
export interface VerplichtCertificaatStandDTO {
  employeeId: string
  eisId: string
  /** De groepsnaam of de typenaam. */
  eisLabel: string
  certificaatGroep: CertificaatGroep | null
  status: CertificaatTypeStatus
  situatie: VerplichtSituatie
  cursusStatus: VerplichtCursusStatus
  /** Gepland of gevolgd op (ISO), of null. */
  cursusDatum: string | null
  cursusNaam: string | null
  /** Het type dat de status draagt: bij een groep de variant. */
  leidendTypeId: string | null
  leidendTypeNaam: string | null
  geldigTot: string | null
  /** Hele kalendermaanden in dienst; null = startdatum onbekend. */
  maandenInDienst: number | null
  deadline: string | null
  laatsteSignaalOp: string | null
  uploadVerzoekOp: string | null
  uploadVerzoekAantal: number
  snoozeTot: string | null
  nietVanToepassing: boolean
  nietVanToepassingReden: string | null
  afwijzingsReden: string | null
  berekendOp: string
}

// ─── HRM-tab "Verplicht" ────────────────────────────────────────────────────

/** Een cel in de matrix: de stand zonder de medewerker- en eisvelden die de rij/kolom al draagt. */
export type VerplichtMatrixCelDTO = Omit<VerplichtCertificaatStandDTO, 'employeeId' | 'eisLabel' | 'certificaatGroep'>

export interface VerplichtMatrixRijDTO {
  employeeId: string
  employeeNumber: string | null
  firstName: string
  lastName: string
  functionTitle: string | null
  employeeType: string | null
  departmentId: string | null
  departmentNaam: string | null
  maandenInDienst: number | null
  /** Per eisId; een eis zonder rij is voor deze medewerker nog niet gemeten. */
  cellen: Record<string, VerplichtMatrixCelDTO>
}

/** De tellingen op de tab Verplicht: server-side over de volledige gefilterde set. */
export interface VerplichtKpisDTO {
  ontbreektTotaal: number
  uploadVerzoekenOpen: number
  cursussenTePlannen: number
  wachtOpControle: number
}

export interface VerplichtMatrixMetaDTO {
  total: number
  page: number
  pageSize: number
  kpis: VerplichtKpisDTO
  /** De kolommen: de actieve, verplichte eisen. */
  eisen: VerplichtCertificaatDTO[]
  /** Filteropties: afdelingen en functietitels van wie in de stand staat. */
  afdelingen: Array<{ id: string; naam: string }>
  functies: string[]
  uploadVerzoekenActief: boolean
  /** True voor een teamleider: de lijst toont alleen het eigen team. */
  alleenEigenTeam: boolean
}

export const verplichtMatrixQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  zoek: z.string().trim().max(100).optional(),
  departmentId: z.string().min(1).optional(),
  functie: z.string().trim().max(120).optional(),
  eisId: z.string().min(1).optional(),
  situatie: z.enum(VERPLICHT_SITUATIES).optional(),
  /** Alleen met minstens zoveel kalendermaanden in dienst. */
  minMaanden: z.coerce.number().int().min(0).max(1200).optional(),
  /** Alleen met maximaal zoveel kalendermaanden in dienst. */
  maxMaanden: z.coerce.number().int().min(0).max(1200).optional(),
  /** Alleen waar een uploadverzoek openstaat (de KPI-tegel; definitie van `isUploadVerzoekOpen`). */
  verzoekOpen: queryBoolean().optional(),
})
export type VerplichtMatrixQuery = z.infer<typeof verplichtMatrixQuerySchema>

/** De filters die in de URL van de tab staan. */
export const VERPLICHT_MATRIX_FILTER_SLEUTELS = [
  'zoek',
  'departmentId',
  'functie',
  'eisId',
  'situatie',
  'minMaanden',
  'maxMaanden',
  'verzoekOpen',
] as const

const redenSchema = z
  .string()
  .trim()
  .min(3, 'Geef een reden op (minimaal 3 tekens)')
  .max(500, 'Maximaal 500 tekens')

export const verplichtItemSchema = z.object({
  employeeId: z.string().min(1),
  eisId: z.string().min(1),
})
export type VerplichtItem = z.infer<typeof verplichtItemSchema>

const verplichtItemsSchema = z.array(verplichtItemSchema).min(1, 'Selecteer minstens een medewerker').max(1000)

/** Bulk: uploadverzoek sturen of cursus aanvragen voor een selectie. */
export const verplichtBulkSchema = z.object({ items: verplichtItemsSchema })
export type VerplichtBulkInput = z.infer<typeof verplichtBulkSchema>

/** N.v.t. voor een selectie: altijd met een reden (nooit stil, spec 241 §5). */
export const verplichtNvtSchema = z.object({ items: verplichtItemsSchema, reden: redenSchema })
export type VerplichtNvtInput = z.infer<typeof verplichtNvtSchema>

/** N.v.t. terugdraaien: ook dat is een besluit, dus met een reden. */
export const verplichtNvtHerstelSchema = z.object({ items: verplichtItemsSchema, reden: redenSchema })
export type VerplichtNvtHerstelInput = z.infer<typeof verplichtNvtHerstelSchema>

/** Snooze: altijd met een reden. Zonder `dagen` de instelling "Later herinneren". */
export const verplichtSnoozeSchema = z.object({
  items: verplichtItemsSchema,
  reden: redenSchema,
  dagen: z.number().int().min(1).max(365).optional(),
})
export type VerplichtSnoozeInput = z.infer<typeof verplichtSnoozeSchema>

/** De uitkomst van een bulkactie: per item verwerkt of overgeslagen met de reden. */
export interface VerplichtBulkResultaatDTO {
  verwerkt: number
  overgeslagen: Array<{ employeeId: string; eisId: string; reden: string }>
}

// ─── Instelling en dry-run ──────────────────────────────────────────────────

/** Eén omzetting van de signalering: wie, wanneer en waarom. */
export interface VerplichtAkkoordHistorieDTO {
  actief: boolean
  doorNaam: string | null
  op: string
  reden: string
}

/**
 * De instelling van de signalering. `uploadVerzoekenActief` staat standaard UIT: tot het
 * HR-akkoord op het dry-run-rapport gaat er niets uit (spec 241 §8).
 */
export interface VerplichtInstellingDTO {
  /**
   * De signalering staat aan: uploadverzoeken aan medewerkers, de weekbundel en de meldingen aan HR
   * en de teamleider. Vóór het HR-akkoord gaat er niets uit (spec 241 §8).
   */
  uploadVerzoekenActief: boolean
  /** Wie het dry-run-rapport akkoord gaf, en wanneer. */
  akkoordDoorId: string | null
  akkoordDoorNaam: string | null
  akkoordOp: string | null
  /** Elke omzetting, nieuwste eerst (maximaal 20): ook wie de eerste ronde vrijgaf blijft zichtbaar. */
  historie: VerplichtAkkoordHistorieDTO[]
  herhaalDagen: number
  maxHerinneringen: number
}

export const verplichtInstellingSchema = z.object({
  herhaalDagen: z.number().int().min(1).max(365),
  maxHerinneringen: z.number().int().min(0).max(12),
})
export type VerplichtInstellingInput = z.infer<typeof verplichtInstellingSchema>

/** Het akkoord op het dry-run-rapport, of het weer uitzetten (met reden). */
export const verplichtAkkoordSchema = z.object({
  actief: z.boolean(),
  reden: redenSchema,
})
export type VerplichtAkkoordInput = z.infer<typeof verplichtAkkoordSchema>

export interface VerplichtDryRunEisDTO {
  eisId: string
  label: string
  /** Een voorbeeldregel die HR nog niet heeft overgenomen. */
  isVoorbeeld: boolean
  /** Na hoeveel maanden in dienst het uploadverzoek komt; null = alleen signaleren. */
  herinneringNaMaanden: number | null
  /** Eerste verzoeken aan de medewerker (zouden uitgaan). */
  uploadVerzoeken: number
  /** Geen portal-toegang: het verzoek gaat naar de teamleider en HR. */
  uploadVerzoekenTeamleider: number
  /** Herinneringen aan de medewerker. */
  herinneringen: number
  /** Komen in de weekbundel "te plannen". */
  tePlannen: number
  /** Startdatum onbekend: HR-signaal, geen verzoek. */
  startOnbekend: number
  /** Geen signaal: geldig, gepland, n.v.t., gesnoozed, ... */
  geenSignaal: number
}

export interface VerplichtDryRunRegelDTO {
  employeeId: string
  naam: string
  eisId: string
  eisLabel: string
  maandenInDienst: number | null
  soort: VerplichtSignaalSoort
  ontvanger: VerplichtSignaalOntvanger
}

export type VerplichtSignaalOntvanger = 'MEDEWERKER' | 'HR_TL' | 'HR'

/** Het rapport van spec 241 §8 (T11): wat zou er uitgaan. Berekend, er wordt niets verstuurd. */
export interface VerplichtDryRunDTO {
  berekendOp: string
  instelling: VerplichtInstellingDTO
  medewerkers: number
  perEis: VerplichtDryRunEisDTO[]
  /** Alle regels van wat naar de medewerker of de teamleider zou gaan. */
  regels: VerplichtDryRunRegelDTO[]
}

// ─── Portal ─────────────────────────────────────────────────────────────────

/** Een eis in de portal (`meta.eisen` op `GET /me/certificaten-overzicht`). */
export interface MijnVerplichteEisDTO {
  eisId: string
  label: string
  certificaatGroep: CertificaatGroep | null
  status: CertificaatTypeStatus
  situatie: VerplichtSituatie
  /** De chip-tekst (verplichtSituatieTekst). */
  situatieTekst: string
  cursusStatus: VerplichtCursusStatus
  cursusDatum: string | null
  cursusNaam: string | null
  geldigTot: string | null
  deadline: string | null
  /** De variant die de status draagt. */
  leidendTypeNaam: string | null
  /** Er staat een uploadverzoek open: de vraag aan de medewerker. */
  uploadVerzoekOpen: boolean
  /** De reden als HR een aangeleverd concept afwees (spec 241 §6.2). */
  afwijzingsReden: string | null
  /** De geaccepteerde varianten, met uitleg: de keuze bij het uploaden. */
  varianten: VerplichtVariantDTO[]
  magUploaden: boolean
  magCursusAanvragen: boolean
}

export interface MijnCertificatenMetaDTO {
  eisen: MijnVerplichteEisDTO[]
}

/** "Ik heb deze cursus nog niet gevolgd": optioneel een toelichting van de medewerker. */
export const meNogNietGevolgdSchema = z.object({
  toelichting: z.string().trim().max(500).optional(),
})
export type MeNogNietGevolgdInput = z.infer<typeof meNogNietGevolgdSchema>

/** De typesuggestie bij een upload: uit de bestandsnaam of de tekst van de PDF. */
export interface CertificaatTypeSuggestieDTO {
  certificateTypeId: string
  naam: string
  code: string
  via: 'BESTANDSNAAM' | 'DOCUMENT'
}

/** De keuzelijst bij een losse upload: de synoniemen maken hem doorzoekbaar (spec 241 §D9). */
export interface MeCertificaatTypeOptieDTO {
  id: string
  name: string
  code: string
  synoniemen: string[]
}
